// §14 / §11.7 — the desktop's background, whichever kind it is.
//
// D-043's wallpapers are pure functions of a name, so the setting holding a name is enough to
// restore one exactly. §11.7's "Set as wallpaper" breaks that symmetry: a photograph is bytes in
// the VFS, and the setting holds its *path* (D-169). Resolving that path to something CSS can use
// is asynchronous and needs cleaning up, which is why it is a hook rather than a function every
// skin's `DesktopSurface` calls inline.
import { useEffect, useState } from 'react';
import { useKernel, useSetting } from '@/kernel/react';
import { wallpaperCss, wallpaperFilePath, wallpaperImageCss } from '@/assets/wallpaper';

/** A `background` shorthand for the current wallpaper setting. */
export function useWallpaperBackground(): string {
  const kernel = useKernel();
  const setting = useSetting('wallpaper');
  const [background, setBackground] = useState(() => wallpaperCss(setting));

  useEffect(() => {
    const path = wallpaperFilePath(setting);
    if (!path) {
      setBackground(wallpaperCss(setting));
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    void kernel.vfs
      .read(path)
      .then((bytes) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([bytes as BlobPart]));
        setBackground(wallpaperImageCss(objectUrl));
      })
      // A wallpaper whose file has gone is not an error worth a dialog: the desktop falls back to
      // a generated one, which is always available because it is a function rather than a file.
      .catch(() => {
        if (!cancelled) setBackground(wallpaperCss(''));
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [kernel, setting]);

  return background;
}
